import { useCallback, useEffect, useState } from 'react';

interface UseControlledInputData<T> {
  isControlled: boolean;
  value: T | undefined;
  syncInputValue: (value: T) => void;
  commitInputValue: (value: T) => void;
}

/**
 * Controlled input scenario (if the value is provided)
 * - The input is controlled and the internal value is not updated
 * - The customer is notified of the change and should handle the value change
 * - `initialValue` property has no effect
 *
 * Uncontrolled input scenario (if the value is not provided)
 * - The input is uncontrolled and the internal value is updated
 * - The internal value is updated when the value prop changes
 * - The customer is notified of the change
 * - `initialValue` property controls the first render value
 */
export const useControlledInput = <T extends string | number | undefined>(
  value: T,
  initialValue: T
): UseControlledInputData<T> => {
  const isControlled = value !== undefined;
  const [inputValue, setInputValue] = useState(initialValue);

  const syncInputValue: UseControlledInputData<T>['syncInputValue'] =
    useCallback(
      (value) => {
        if (!isControlled) {
          setInputValue(value);
        }
      },
      [isControlled]
    );

  const commitInputValue: UseControlledInputData<T>['commitInputValue'] =
    useCallback(
      (value) => {
        if (!isControlled) {
          setInputValue(value ?? initialValue);
        }
      },
      [isControlled, initialValue]
    );

  useEffect(() => {
    if (initialValue !== undefined) {
      syncInputValue(initialValue);
    }
  }, [syncInputValue, initialValue]);

  return {
    isControlled,
    value: isControlled ? value : inputValue,
    syncInputValue,
    commitInputValue,
  };
};
